// Save layer (ENG-9/10, D9). World CRUD + game save/load. Only edited sections
// are persisted (RLE-compressed); pristine chunks regenerate from the seed. On
// any IndexedDB failure (quota/private mode) it degrades to memory mode — the
// game still runs, saves are no-ops, and a banner warns (E-21).
import {
  openDB,
  idbGet,
  idbGetAll,
  idbGetAllKeys,
  idbPut,
  idbPutMany,
  idbDelete,
  idbClearRange,
  type StoreName,
} from './idb';
import type { PlayerSave } from '../../sim/game/player';
import type { SavedEntity } from '../../sim/entities/entity';
import type { SavedBlockEntity } from '../../sim/game/blockentity';
import type { StatsSave } from '../../sim/game/stats';
import type { AdvancementSave } from '../../sim/game/advancements';
import { DEFAULT_RANGE, type YRange } from '../../sim/engine/section';
import type { Notebook } from '../../sim/heredity/notebook';
import type { LessonResult, WorldLesson, LessonLogEntry } from '../../sim/education/results';
import {
  CURRENT_SAVE_VERSION, migrateWorldMeta, migratePlayerState, absoluteSectionY, sectionArrayIndex,
} from './migrate';

export type WorldType = 'normal' | 'flat' | 'atlas' | 'earth';

/** An Earth world's source (ERT-1): a real lat/lon voxelized from TIL tiles with a
 *  sea-level datum. `scaleM` (metres/block) drives the world's Y-range; `demSource`
 *  and `contract` come back from the resolved tile set (the accuracy badge, ERT-3). */
export interface EarthMeta {
  lat: number;
  lon: number;
  label: string;
  /** Metres per block (default 0.9144 = 3 ft, per-world editable). */
  scaleM: number;
  /** Bounded extent (m from centre); beyond → flat sea-level ocean (ERT-6). */
  extentM: number;
  /** Tile service (non-default worlds); the default boots the bundled fixture. */
  contract?: { url?: string; major: number };
  /** DEM provenance stamped from the resolved tiles — drives the badge (ERT-3). */
  demSource?: string;
}

export interface WorldMeta {
  id: string;
  name: string;
  seed: string;
  worldType: WorldType;
  gameMode: 'survival' | 'creative';
  difficulty: 'peaceful' | 'easy' | 'normal' | 'hard';
  /** Hardcore: death locks the world (M4.4). */
  hardcore?: boolean;
  /** Allow cheats / commands in this world (independent of game mode). When
   *  absent (pre-M5 saves) the runtime falls back to `gameMode !== 'survival'`. */
  allowCommands?: boolean;
  /** Atlas-world source (ATL-1); the world is voxelized from this on load. */
  atlas?: { location?: string; lat?: number; lon?: number; scale: number; simulated?: boolean };
  /** Earth-world source (ERT-1); resolved into TIL tiles + a scale-derived range. */
  earth?: EarthMeta;
  /** Reference overlays (OVL-1): client-only ghost planes, never sim state (OVL-2/3).
   *  Small placements live here; the image blobs live in the `overlays` IDB store. */
  overlays?: OverlayPlacement[];
  /** Baked imagery (OVL-4, M43): aligned NAIP tiles / user overlays PINNED (sha-256) as
   *  deterministic generator input — the ENG-1 crossing (overlays stop being ghosts and
   *  become ground). Absent ⇒ no bake (generation byte-identical to M40). A re-bake
   *  rewrites this = a declared regen event for pristine chunks only. */
  imageryBake?: ImageryBake;
  /** Bounded child world (DOR-3/4, M41): a square footprint centred on the origin.
   *  `size` blocks per side → valid columns |x|,|z| < size/2; beyond is the world edge
   *  (barrier/ocean at gen). Absent ⇒ unbounded (every world before M41). */
  bounds?: { size: number };
  /** The world this one was created from (DOR-5, M41): the pause-menu escape hatch
   *  walks this chain up to a reachable root so a player is never stranded. Root/
   *  top-level worlds have none. */
  homeWorldId?: string;
  /** Return doors to materialise on first entry (DOR-1, M41): binding a door registers
   *  the paired back-edge here; the target world places a real `world_door` at its spawn
   *  the first time it loads, then clears the entry. */
  pendingDoors?: { doorId: string; targetWorldId: string; targetDoorId: string }[];
  /** Aviation layer (Part VI / M32, AVI-2): opt-in flyable aircraft on Atlas worlds.
   *  Off by default; when on with no OSM runway a sandbox airstrip is synthesized. */
  aviation?: boolean;
  /** Flat-world preset id (M24 world-create v2); remaps the flat surface blocks. */
  flatPreset?: string;
  /** Game rules chosen at creation (M24); re-applied to the world on every load. */
  gamerules?: Record<string, string>;
  /** Per-dimension Y-ranges (v2 / TAL-4). Absent ⇒ the default −64..319 range for
   *  every dimension; a tall/Earth dimension records its own {minY,height} so a
   *  loader can reconstruct the column shape without a live world. */
  ranges?: Record<string, { minY: number; height: number }>;
  /** The world's single active lesson (EDU-2, ARCH §29.3; save v5). Absent ⇒ the
   *  evaluator is not registered — parity by construction (D21). */
  lesson?: WorldLesson;
  /** The instructor console's action log (EDU-6, ARCH §29.8; save v5), capped at
   *  `EDU.LOG_MAX`. Absent until the first freeze/eyes-up/teleport/bring. */
  lessonLog?: LessonLogEntry[];
  /** The shared-screen policy gate (COW-10, ARCH §30.6; save v6): `editor`+ may
   *  share here. Owner-set (`/coworking adultsOnly on|off`), mutually exclusive
   *  with an active lesson (E-66). Default `false` (minors assumed, D-TF-111). */
  adultsOnly?: boolean;
  /** Hangar §39.7 (M107.3): this world's linked task boards. Client storage, not sim
   *  state — a board is linked to one Hangar Reminders list at a time, and the server
   *  keeps only the narrower "is this cell linked at all" fact (`POST /world/board-
   *  link`) a reach-waiver security check needs, never this full mapping. Absent on
   *  every pre-M107.3 save ⇒ no linked boards, same as a fresh world. */
  boardLinks?: BoardLink[];
  createdAt: number;
  playedAt: number;
  version: number;
}

/** Hangar §39.7 (M107.3) — one `task_board` cell's link to a Hangar Reminders list.
 *  `mapping` is the `lineId ↔ reminderItemId` correspondence the reconcile pass diffs
 *  against; it is rewritten wholesale on every reconcile, never patched entry-by-entry,
 *  so a line/item removed on either side simply has no row here afterward. */
export interface BoardLink {
  boardCell: { x: number; y: number; z: number };
  listId: string;
  /** The last-reconciled snapshot each pair agreed on (`hangar/boardsync.ts`'s own
   *  `SyncMapping`, structurally duplicated — this file is eager and `hangar/**` is
   *  the NET-7-lazy boundary, so the shape is repeated rather than imported). */
  mapping: { lineId: string; reminderItemId: string; text: string; done: boolean; updatedAt: number }[];
}

/** A pinned imagery bake (OVL-4, M43). Each entry hashes the SOURCE bytes so the same
 *  bake reproduces byte-identically; on load the source is re-fetched, re-hashed, and a
 *  mismatch drops that entry (never a silent re-bake). */
export interface ImageryBake {
  /** Pinned NAIP tiles (M43.1): slippy z/x/y + sha-256 of the source PNG (sidecar sha). */
  tiles?: { z: number; x: number; y: number; hash: string }[];
  /** Pinned baked user overlays (M43.2): the overlay id, its image hash, and the block
   *  placement it was aligned to (a snapshot — later ghost nudges don't move the bake). */
  overlays?: { overlayId: string; hash: string; anchor: { x: number; y: number; z: number }; widthBlocks: number; heightBlocks: number }[];
  bakedAt: number;
}

/** A placed reference overlay (OVL-1): a horizontal image plane anchored at its
 *  NW/top-left corner. Client render chrome only — the sim never reads it (OVL-2/3). */
export interface OverlayPlacement {
  overlayId: string;
  /** The image's NW/top-left corner in world blocks (y = the plane's height). */
  anchor: { x: number; y: number; z: number };
  /** Ground width of the image in blocks (height derived from the image aspect). */
  widthBlocks: number;
  opacity: number;
  hidden: boolean;
}

/**
 * Player progression persisted with the world (M25 ENG-11): the three M24
 * trackers (statistics, advancements, recipe book) that shipped with toJSON/load
 * but were deliberately left unwired until the save-format freeze. Absent on every
 * pre-M25 save → the migration harness fills an empty bundle.
 */
export interface WorldProgress {
  readonly version: number;
  readonly stats: StatsSave;
  readonly advancements: AdvancementSave;
  readonly recipeBook: readonly string[];
}

export interface PlayerState {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  dim: string;
  /** Survival state + inventory (M4). Absent on pre-M4 saves → fresh player. */
  survival?: PlayerSave;
  /** Statistics / advancements / recipe-book progress (M25 ENG-11). */
  progress?: WorldProgress;
  /** The field notebook's records (HER-7, ARCH §28.11; save v4). Absent until the
   *  player first records something — absent on every pre-v4 save. */
  notebook?: Notebook;
  /** Per-lesson results, keyed by lesson `name` (EDU-8, ARCH §29.6; save v5).
   *  Absent until the player first joins a lesson world — absent on every pre-v5
   *  save. */
  lessons?: Record<string, LessonResult>;
}

interface SectionRow {
  cx: number;
  cz: number;
  sy: number;
  rle: Uint16Array;
}

export interface LoadedSection {
  cx: number;
  cz: number;
  sy: number;
  states: Uint16Array | null;
}

const SAVE_VERSION = CURRENT_SAVE_VERSION; // ENG-11: the frozen v1.0 format version
const SEP = '\u0000'; // null: sorts lowest so worldRange bounds an id; never in world ids/dims

function encodeRLE(states: Uint16Array): Uint16Array {
  const out: number[] = [];
  let i = 0;
  while (i < states.length) {
    const v = states[i]!;
    let n = 1;
    while (i + n < states.length && states[i + n] === v && n < 65535) n++;
    out.push(n, v);
    i += n;
  }
  return Uint16Array.from(out);
}

function decodeRLE(rle: Uint16Array): Uint16Array {
  const out = new Uint16Array(4096);
  let p = 0;
  for (let i = 0; i + 1 < rle.length; i += 2) {
    const n = rle[i]!;
    out.fill(rle[i + 1]!, p, p + n);
    p += n;
  }
  return out;
}

const sectionKey = (id: string, dim: string, cx: number, cz: number, sy: number): string =>
  `${id}${SEP}${dim}${SEP}${cx}${SEP}${cz}${SEP}${sy}`;

/** Key range covering every row for a world across the composite-key stores. */
function worldRange(id: string): IDBKeyRange {
  return IDBKeyRange.bound(`${id}${SEP}`, `${id}${SEP}￿`);
}

/** The slice of `SaveManager` a `.tfworld` export/import needs (M51.6). A plain
 *  structural type, not an explicit implements: a real `SaveManager` satisfies it
 *  for free, and a test double can satisfy it with no IndexedDB at all — the same
 *  reason the server side already narrows storage to `StoragePort` rather than a
 *  concrete class. */
export type WorldFileStore = Pick<SaveManager,
  'getWorld' | 'loadSections' | 'loadBlockEntities' | 'putWorld' | 'putSections' | 'saveBlockEntities'
  | 'loadEntities' | 'saveEntities'>;

export class SaveManager {
  private constructor(private readonly db: IDBDatabase | null) {}

  get memoryMode(): boolean {
    return this.db === null;
  }

  static async open(): Promise<SaveManager> {
    try {
      return new SaveManager(await openDB());
    } catch (e) {
      console.warn('[saves] IndexedDB unavailable — running in memory mode (E-21):', e);
      return new SaveManager(null);
    }
  }

  async listWorlds(): Promise<WorldMeta[]> {
    if (!this.db) return [];
    const all = await idbGetAll<unknown>(this.db, 'worlds');
    // ENG-11: older-era records migrate up on read; drop any that are unrecoverable.
    const migrated = all.map(migrateWorldMeta).filter((m): m is WorldMeta => m !== null);
    return migrated.sort((a, b) => b.playedAt - a.playedAt);
  }

  async getWorld(id: string): Promise<WorldMeta | undefined> {
    if (!this.db) return undefined;
    const raw = await idbGet<unknown>(this.db, 'worlds', id);
    return migrateWorldMeta(raw) ?? undefined; // ENG-11: migrate on read
  }

  async putWorld(meta: WorldMeta): Promise<void> {
    if (!this.db) return;
    await this.guard(() => idbPut(this.db!, 'worlds', meta.id, meta));
  }

  async getPlayer(id: string): Promise<PlayerState | undefined> {
    if (!this.db) return undefined;
    const raw = await idbGet<unknown>(this.db, 'player', id);
    return migratePlayerState(raw); // ENG-11: migrate on read (fills empty progress on pre-M25 saves)
  }

  async loadSections(id: string, dim: string, range: YRange = DEFAULT_RANGE): Promise<LoadedSection[]> {
    if (!this.db) return [];
    await this.upgradeSectionsToV2(id); // idempotent v1→v2 re-key before reading (TAL-4)
    const rows = await idbGetAll<SectionRow>(this.db, 'sections', worldRange(id));
    // dim is encoded in the key; getAll returns all dims for the world, but M1.4
    // only has 'overworld', and rows carry cx/cz/sy directly.
    void dim;
    // Stored `sy` is absolute signed section-Y (v2); the runtime array is indexed
    // by the dimension's offset-baked index (TAL-4).
    return rows.map((r) => ({
      cx: r.cx, cz: r.cz, sy: sectionArrayIndex(r.sy, range),
      states: r.rle.length ? decodeRLE(r.rle) : null,
    }));
  }

  /** Persist meta (playedAt bumped + range recorded), player, and all dirty
   *  sections in batched transactions. Section rows are keyed by absolute signed
   *  section-Y (v2 / TAL-4); `range` is the saving dimension's Y-range. */
  async saveGame(
    meta: WorldMeta,
    player: PlayerState,
    dirty: Array<{ cx: number; cz: number; sy: number; states: Uint16Array | null }>,
    range: YRange = DEFAULT_RANGE,
  ): Promise<void> {
    if (!this.db) return;
    await this.guard(async () => {
      const db = this.db!;
      const ranges = { ...meta.ranges, [player.dim]: { minY: range.minY, height: range.height } };
      await idbPut(db, 'worlds', meta.id, { ...meta, ranges, playedAt: Date.now(), version: SAVE_VERSION });
      await idbPut(db, 'player', meta.id, player);
      await this.writeSections(db, meta.id, player.dim, dirty, range);
    });
  }

  /** Persist a batch of edited sections with no player row involved — the
   *  primitive `saveGame` itself uses, exposed for a world that has terrain
   *  before it has a player (M51.6: a lesson-template import). Keyed exactly like
   *  `saveGame`'s own sections (absolute signed section-Y, v2 / TAL-4). */
  async putSections(
    id: string, dim: string,
    dirty: Array<{ cx: number; cz: number; sy: number; states: Uint16Array | null }>,
    range: YRange = DEFAULT_RANGE,
  ): Promise<void> {
    if (!this.db) return;
    await this.guard(() => this.writeSections(this.db!, id, dim, dirty, range));
  }

  private async writeSections(
    db: IDBDatabase, id: string, dim: string,
    dirty: Array<{ cx: number; cz: number; sy: number; states: Uint16Array | null }>,
    range: YRange,
  ): Promise<void> {
    if (!dirty.length) return;
    const entries: Array<[IDBValidKey, SectionRow]> = dirty.map((d) => {
      const sy = absoluteSectionY(d.sy, range); // array index → absolute section-Y
      return [
        sectionKey(id, dim, d.cx, d.cz, sy),
        { cx: d.cx, cz: d.cz, sy, rle: d.states ? encodeRLE(d.states) : new Uint16Array(0) },
      ];
    });
    await idbPutMany(db, 'sections', entries);
  }

  /**
   * One-shot v1→v2 section re-key (TAL-4). v1 rows were keyed by the offset-baked
   * array index; v2 keys them by absolute signed section-Y so a MIN_Y change can
   * never silently reindex a save. Idempotent — gated on the STORED world version
   * (< 2), so it runs at most once per world. v1 worlds are all default-range, so
   * the shift is a uniform −4. Best-effort + guarded (E-21), matching saveGame's
   * non-atomicity: a rare crash mid-migration leaves the version unbumped and the
   * next open retries.
   */
  private async upgradeSectionsToV2(id: string): Promise<void> {
    if (!this.db) return;
    const db = this.db;
    const raw = await idbGet<{ version?: number }>(db, 'worlds', id);
    if (!raw || (typeof raw.version === 'number' && raw.version >= 2)) return; // already v2 / fresh
    await this.guard(async () => {
      const kr = worldRange(id);
      const keys = await idbGetAllKeys(db, 'sections', kr);
      const rows = await idbGetAll<SectionRow>(db, 'sections', kr); // parallel to keys (key order)
      const puts: Array<[IDBValidKey, SectionRow]> = [];
      for (let i = 0; i < keys.length; i++) {
        const row = rows[i]!;
        const dim = String(keys[i]).split(SEP)[1] ?? 'overworld';
        const sy = absoluteSectionY(row.sy); // v1 default range → absolute
        puts.push([sectionKey(id, dim, row.cx, row.cz, sy), { cx: row.cx, cz: row.cz, sy, rle: row.rle }]);
      }
      // Clear the old array-index keys, then write the absolute-keyed rows.
      await idbClearRange(db, 'sections', kr);
      if (puts.length) await idbPutMany(db, 'sections', puts);
      const migrated = migrateWorldMeta(raw); // re-stamp world row to v2 (records nothing new here)
      if (migrated) await idbPut(db, 'worlds', id, migrated);
    });
  }

  /** Persist the near-player entity list for a dimension (ARCH §3.6, MOB-4). */
  async saveEntities(id: string, dim: string, list: SavedEntity[]): Promise<void> {
    if (!this.db) return;
    await this.guard(() => idbPut(this.db!, 'entities', `${id}${SEP}${dim}`, list));
  }

  async loadEntities(id: string, dim: string): Promise<SavedEntity[]> {
    if (!this.db) return [];
    return (await idbGet<SavedEntity[]>(this.db, 'entities', `${id}${SEP}${dim}`)) ?? [];
  }

  /** Persist the dimension's block entities — chests/furnaces/spawners (§3.6, M6). */
  async saveBlockEntities(id: string, dim: string, list: SavedBlockEntity[]): Promise<void> {
    if (!this.db) return;
    await this.guard(() => idbPut(this.db!, 'blockEntities', `${id}${SEP}${dim}`, list));
  }

  async loadBlockEntities(id: string, dim: string): Promise<SavedBlockEntity[]> {
    if (!this.db) return [];
    return (await idbGet<SavedBlockEntity[]>(this.db, 'blockEntities', `${id}${SEP}${dim}`)) ?? [];
  }

  /** Every dimension's block-entity rows for a world (M41 world-graph enumeration).
   *  The composite key encodes the dimension; each row is that dim's saved list. */
  async allBlockEntities(id: string): Promise<{ dim: string; list: SavedBlockEntity[] }[]> {
    if (!this.db) return [];
    const kr = worldRange(id);
    const keys = await idbGetAllKeys(this.db, 'blockEntities', kr);
    const rows = await idbGetAll<SavedBlockEntity[]>(this.db, 'blockEntities', kr); // key-order parallel
    return keys.map((k, i) => ({ dim: String(k).split(SEP)[1] ?? 'overworld', list: rows[i] ?? [] }));
  }

  /** Every dimension's persisted entity rows for a world (M41 bounds scan / metering). */
  async allEntities(id: string): Promise<{ dim: string; list: SavedEntity[] }[]> {
    if (!this.db) return [];
    const kr = worldRange(id);
    const keys = await idbGetAllKeys(this.db, 'entities', kr);
    const rows = await idbGetAll<SavedEntity[]>(this.db, 'entities', kr);
    return keys.map((k, i) => ({ dim: String(k).split(SEP)[1] ?? 'overworld', list: rows[i] ?? [] }));
  }

  /** Every edited section's coordinates for a world (M41 bounds scan / metering). The
   *  stored `sy` is absolute signed section-Y (v2 / TAL-4); callers only need cx/cz. */
  async sectionRows(id: string): Promise<{ dim: string; cx: number; cz: number; sy: number; bytes: number }[]> {
    if (!this.db) return [];
    const kr = worldRange(id);
    const keys = await idbGetAllKeys(this.db, 'sections', kr);
    const rows = await idbGetAll<SectionRow>(this.db, 'sections', kr);
    return keys.map((k, i) => {
      const dim = String(k).split(SEP)[1] ?? 'overworld';
      const r = rows[i]!;
      return { dim, cx: r.cx, cz: r.cz, sy: r.sy, bytes: r.rle.byteLength };
    });
  }

  /** Store a reference-overlay image blob (OVL-1) keyed by its overlay id, as a data
   *  URL. Separate from the small placement in `WorldMeta.overlays[]` (hot metadata). */
  async putOverlayBlob(overlayId: string, dataUrl: string): Promise<void> {
    if (!this.db) return;
    await this.guard(() => idbPut(this.db!, 'overlays', overlayId, dataUrl));
  }

  /** Load a reference-overlay image blob; undefined when missing (dropped at load, E-40). */
  async getOverlayBlob(overlayId: string): Promise<string | undefined> {
    if (!this.db) return undefined;
    return idbGet<string>(this.db, 'overlays', overlayId);
  }

  async deleteOverlayBlob(overlayId: string): Promise<void> {
    if (!this.db) return;
    await this.guard(() => idbDelete(this.db!, 'overlays', overlayId));
  }

  async deleteWorld(id: string): Promise<void> {
    if (!this.db) return;
    await this.guard(async () => {
      const db = this.db!;
      await idbDelete(db, 'worlds', id);
      await idbDelete(db, 'player', id);
      const range = worldRange(id);
      for (const store of ['sections', 'blockEntities', 'entities', 'maps'] as StoreName[]) {
        await idbClearRange(db, store, range);
      }
    });
  }

  /** Run a persistence op; on quota/IO failure, warn and continue (E-21). */
  private async guard(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (e) {
      console.warn('[saves] write failed (continuing):', e);
    }
  }
}
